import type { SignupRegion } from '@server/signup-answers';
import { useEffect, useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Label } from '@/_shared/components/ui/label';
import type { client } from '@/_shared/lib/rpc';

import { type SlugStatus, useWorkspaceSlug } from '../../hooks/useWorkspaceSlug';
import { RegionRadioGroup } from '../nativeSignIn/RegionRadioGroup';
import { WorkspaceUrlField } from './PickerParts';

type PickerOrganization = Awaited<ReturnType<typeof client.workspace.listSiblingPicker>>['organizations'][number];

export interface CreateWorkspaceInput {
  organizationId: string;
  slug: string;
  region: SignupRegion;
}

/** Why Create is disabled for the URL, or null when the URL is available. */
function urlNotReadyReason(status: SlugStatus): string | null {
  switch (status) {
    case 'available':
      return null;
    case 'checking':
      return 'Lookout is checking the workspace URL.';
    case 'taken':
    case 'invalid':
      return 'Pick a workspace URL that is available.';
    case 'idle':
      return 'Enter a workspace URL of 2 or more characters.';
  }
}

/**
 * The create step of the sibling picker (ACT2-1124 LLD §5.1): a dialog for one organization, with the RudderStack
 * region and the workspace URL. The picker owns the create call and its errors.
 */
export function CreateWorkspaceDialog({
  organization,
  pending,
  onCreate,
  onClose,
}: {
  /** The organization to create in. Null closes the dialog. */
  organization: PickerOrganization | null;
  pending: boolean;
  onCreate: (input: CreateWorkspaceInput) => void;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Dialog
      open={organization !== null}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {organization && (
        <DialogContent closeDisabled={pending}>
          <CreateWorkspaceForm
            key={organization.id}
            organization={organization}
            pending={pending}
            onCreate={onCreate}
            onClose={onClose}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

/** Mounted once for each organization, so that every open starts with US and a new URL. */
function CreateWorkspaceForm({
  organization,
  pending,
  onCreate,
  onClose,
}: {
  organization: PickerOrganization;
  pending: boolean;
  onCreate: (input: CreateWorkspaceInput) => void;
  onClose: () => void;
}): React.ReactElement {
  const [region, setRegion] = useState<SignupRegion>('US');
  const slugField = useWorkspaceSlug();
  const label = organization.name ?? organization.id;

  /** A create that has not finished keeps its URL in that region (F4b); the server uses it again. */
  const pendingSlug = (picked: SignupRegion): string | null =>
    organization.pendingCreates.find((p) => p.region === picked)?.slug ?? null;

  const fillSlug = (picked: SignupRegion): void => {
    const unfinished = pendingSlug(picked);
    if (unfinished) slugField.fix(unfinished);
    else void slugField.prefill(label);
  };

  // The first fill, for US. Later fills follow a region change.
  useEffect(() => {
    fillSlug('US');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once for each organization: the form is keyed on it
  }, []);

  const changeRegion = (picked: SignupRegion): void => {
    const wasPending = pendingSlug(region) !== null;
    setRegion(picked);
    if (pendingSlug(picked) !== null || wasPending) fillSlug(picked);
  };

  const unfinished = pendingSlug(region);
  const notReady = urlNotReadyReason(slugField.status);

  return (
    <form
      noValidate
      aria-label={`Create a workspace in ${label}`}
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (notReady === null && !pending) onCreate({ organizationId: organization.id, slug: slugField.slug, region });
      }}
    >
      <DialogHeader>
        <DialogTitle>Create a workspace in {label}</DialogTitle>
        <DialogDescription>Pick the RudderStack region and the URL of the Lookout workspace.</DialogDescription>
      </DialogHeader>
      <div>
        <Label id={`region-${organization.id}`}>RudderStack region</Label>
        <p className="mt-0.5 mb-2 text-xs text-muted-foreground">
          The workspace's RudderStack data stays in this region. You can't change it later.
        </p>
        <RegionRadioGroup
          aria-labelledby={`region-${organization.id}`}
          value={region}
          disabled={pending}
          onValueChange={changeRegion}
        />
      </div>
      <div>
        <WorkspaceUrlField id={`slug-${organization.id}`} field={slugField} readOnly={unfinished !== null || pending} />
        {unfinished !== null && (
          <p className="mt-1 text-xs text-muted-foreground">
            An earlier create in this region did not finish. Lookout finishes it with this URL.
          </p>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="secondary" disabled={pending} onClick={onClose} label="Cancel" />
        <ActionTooltip show={notReady !== null && !pending} message={notReady ?? ''} keepMounted>
          <Button type="submit" variant="primary" disabled={notReady !== null} loading={pending} label="Create" />
        </ActionTooltip>
      </DialogFooter>
    </form>
  );
}
